<template>
  <modal
    id="modal-demo"
    ref="modal"
    v-model="isModalOpen"
    @hide="handleModalClose"
  >
    <div slot="header">
      <h4 v-if="order">Account: {{ order.account }}</h4>
      <h4 v-if="order">Order: {{ order.skOrderNum }}/{{ order.skCustRef }}</h4>
      <div v-if="order" class="font-lg">
        Requested: {{ order.boxesRequested }} cases
      </div>
      <div v-if="order" class="font-lg">
        Short: <span>{{ order.boxesShort }} cases</span> ({{
          order.boxesRequested - order.boxesShort
        }}
        scanned)
      </div>
    </div>

    <div slot="default" class="table-wrapper">
      <table class="table">
        <thead>
          <tr>
            <th>Item #</th>
            <th>Description</th>
            <th>Qty Req</th>
            <th>Qty Short</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="orderLine in shortedOrderLines"
            :key="orderLine.skOrderLineId"
          >
            <td>
              <div>{{ orderLine.custRef }}</div>
              <div>{{ orderLine.offeringNum }}</div>
            </td>
            <td>{{ orderLine.description }}</td>
            <td style="min-width: 75px">{{ orderLine.qtyRequestedText }}</td>
            <td style="min-width: 150px" class="form-inline">
              <div class="form-group case_bottle_qty" style="margin-right: 8px">
                <input
                  type="number"
                  class="form-control"
                  min="0"
                  :max="orderLine.qtyRequestedCases"
                  :id="`${orderLine.skOrderLineId}_cases`"
                  v-model.number="orderLine.qtyShortCases"
                />
                <label :for="`${orderLine.skOrderLineId}_cases`">{{
                  orderLine.displaySuffix || 'c'
                }}</label>
              </div>
              <div class="form-group case_bottle_qty">
                <input
                  type="number"
                  class="form-control"
                  min="0"
                  :max="orderLine.caseQty"
                  :id="`${orderLine.skOrderLineId}_bottles`"
                  v-model.number="orderLine.qtyShortBottles"
                />
                <label :for="`${orderLine.skOrderLineId}_bottles`">bt</label>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      slot="footer"
      style="display: flex; justify-content: space-between; text-align: center"
    >
      <btn @click="isModalOpen = false" :disabled="isSubmitting">Cancel</btn>
      <div v-if="qtyLeft != 0" class="font-lg short">
        {{ qtyLeftText }} left to report
      </div>
      <div v-else class="font-lg">All shorts reported</div>
      <btn v-if="isSubmitting" type="primary" disabled>Publishing...</btn>
      <btn v-else @click="submit" type="primary">Publish Short</btn>
    </div>
  </modal>
</template>

<script>
import {
  cbBalanceQty,
  balanceToCases,
  balanceToBottles,
  cbToBalance,
} from '@/utils';
import { getShortedOrderLines, publishShortedOrder } from '@/queries';

const BOX_CASE_QTY = 12;

export default {
  name: 'PublishShortModal',
  props: ['order'],
  data() {
    return {
      isSubmitting: false,
      isModalOpen: false,
      errors: [], // TODO: render errors
      shortedOrderLines: [],
      qtyExpected: 0,
      qtyReported: 0,
    };
  },
  computed: {
    qtyLeft() {
      return this.qtyExpected - this.qtyReported;
    },
    qtyLeftText() {
      return cbBalanceQty(this.qtyLeft, BOX_CASE_QTY, 0);
    },
  },
  apollo: {
    shortedOrderLines: {
      query: getShortedOrderLines,
      fetchPolicy: 'network-only',
      variables() {
        return {
          orderId: this.order ? this.order.id : null,
        };
      },
      update(data) {
        // Map GQL response into server data needed for the form here
        return data.shortedOrderLines.map((v) => ({
          skOrderLineId: v.skOrderLineId,
          offeringNum: v.offeringNum,
          custRef: v.custRef,
          description: v.description,
          caseQty: v.caseQty,
          displaySuffix: v.displaySuffix || 'c',
          qtyRequestedText: cbBalanceQty(
            v.qtyRequested,
            v.caseQty,
            0,
            true,
            false,
            false,
            v.displaySuffix
          ),
          qtyRequestedCases: balanceToCases(v.qtyRequested, v.caseQty),
          qtyShortCases: balanceToCases(v.qtyShort, v.caseQty) || '',
          qtyShortBottles: balanceToBottles(v.qtyShort, v.caseQty) || '',
        }));
      },
      skip() {
        return this.order === null;
      },
      error({ networkError, graphQLErrors }) {
        if (networkError) {
          this.errors.push(
            'There was a network error. Please refresh the page and try again.'
          );
        }
        if (graphQLErrors.length > 0) {
          graphQLErrors.forEach((error) => {
            this.errors.push(error.message);
          });
        }
      },
      watchLoading(isLoading) {
        if (!isLoading) {
          this.isModalOpen = true;
        }
      },
    },
  },
  watch: {
    order(value) {
      if (value) {
        this.qtyExpected = cbToBalance(value.boxesShort, 0, BOX_CASE_QTY);
      }
    },
    shortedOrderLines: {
      deep: true,
      handler(value) {
        if (!value.length) {
          return;
        }
        this.updateReportedQty(value);
      },
    },
  },
  methods: {
    handleModalClose() {
      this.$emit('onClose');
    },
    updateReportedQty(shortedOrderLines) {
      this.qtyReported = shortedOrderLines.reduce(
        (sum, ol) =>
          sum + cbToBalance(ol.qtyShortCases, ol.qtyShortBottles, BOX_CASE_QTY),
        0
      );
    },
    async submit() {
      if (this.qtyLeft !== 0) {
        const isConfirmed = confirm(
          `There is ${this.qtyLeftText} left to report. Do you want to continue?`
        );
        if (!isConfirmed) {
          return;
        }
      }

      try {
        this.isSubmitting = true;

        const { data } = await this.$apollo.mutate({
          mutation: publishShortedOrder,
          variables: {
            input: {
              orderId: this.order.id,
              orderLines: this.shortedOrderLines.map((orderLine) => {
                return {
                  skOrderLineId: orderLine.skOrderLineId,
                  qtyShort: cbToBalance(
                    orderLine.qtyShortCases,
                    orderLine.qtyShortBottles,
                    orderLine.caseQty
                  ),
                };
              }),
            },
          },
        });

        // Only close modal after a success
        // An error message should be rendered in 'catch',
        // Then the user can close the modal themselves
        this.isModalOpen = false;
      } catch (e) {
        this.errors.push(e.message);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.short {
  color: red;
}

.font-lg {
  font-size: 1.2em;
}

/* Table stuff */
.table-wrapper {
  max-height: 500px;
  overflow: auto;
}

/* Set header to stick to the top of the container. */
thead tr th {
  position: sticky;
  top: 0;
}

/* If we use border,
we must use table-collapse to avoid
a slight movement of the header row */
table {
  border-collapse: collapse;
}

/* Because we must set sticky on th,
 we have to apply background styles here
 rather than on thead */
th {
  background: white;
  /* With border-collapse, we must use box-shadow or psuedo elements
  for the header borders */
}
table.table tbody > tr > td {
  vertical-align: middle;
}

/* Input stuff */

.case_bottle_qty input::-webkit-outer-spin-button,
.case_bottle_qty input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.case_bottle_qty input[type='number'] {
  -moz-appearance: textfield;
}

.case_bottle_qty input:invalid {
  border-color: #b94a48;
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
}

.case_bottle_qty input {
  width: 50px;
}

.case_bottle_qty label {
  font-weight: 200;
}
</style>



// WEBPACK FOOTER //
// src/components/PublishShortModal.vue